import { createAsyncThunk, createSlice, isFulfilled, isPending } from '@reduxjs/toolkit';
import axios from 'axios';

import { IMaintenanceVehicleDTO, defaultValue } from 'app/shared/model/dto/maintenance-vehicle-dto.model';
import { IMaintenanceVehicle } from 'app/shared/model/maintenance-vehicle.model';
import { IQueryParams, serializeAxiosError } from 'app/shared/reducers/reducer.utils';
import { cleanEntity } from 'app/shared/util/entity-utils';

const initialState = {
  loading: false,
  errorMessage: null,
  entities: [] as IMaintenanceVehicle[],
  entity: defaultValue as IMaintenanceVehicle,
  maintenanceVehicleDTO: {} as IMaintenanceVehicleDTO,
  updating: false,
  totalItems: 0,
  updateSuccess: false,
};

export type MaintenanceVehicleDTOState = Readonly<typeof initialState>;

const apiUrl = 'api/maintenance-vehicles-custom';

// Actions

type Params = { maintenanceControlId?: string | number | null; truckImplementId: string | number };
export const getMaintenanceVehicleByMaintenanceControlIdAndTruckImplementId = createAsyncThunk(
  'maintenanceVehicleCustomDTOByMaintenanceControl/fetch_entity',
  async ({ maintenanceControlId, truckImplementId }: Params) => {
    const params = maintenanceControlId ? `&maintenanceControlId=${maintenanceControlId}` : '';
    const requestUrl = `${apiUrl}/by-maintenance-control?truckImplementId=${truckImplementId}${params}`;
    return axios.get<IMaintenanceVehicleDTO>(requestUrl);
  }
);

export const getMaintenanceVehicleById = createAsyncThunk('maintenanceVehicleDTO/fetch_entity', async (id: string | number) => {
  const requestUrl = `${apiUrl}/by-maintenance-vehicle/${id}`;
  return axios.get<IMaintenanceVehicleDTO>(requestUrl);
});

interface IPagedMaintenanceVehicleQueryParams extends IQueryParams {
  nameSearch?: string;
}

export const getEntities = createAsyncThunk('maintenanceVehicleDTO/fetch_entity_list', async ({ page, size, sort, nameSearch }: IPagedMaintenanceVehicleQueryParams) => {
  const requestUrl = `${apiUrl}${sort ? `?page=${page}&size=${size}&sort=${sort}&nameSearch=${nameSearch}&` : '?'}cacheBuster=${new Date().getTime()}`;
  return axios.get<IMaintenanceVehicleDTO[]>(requestUrl);
});

export const getEntity = createAsyncThunk(
  'maintenanceVehicle/fetch_entity',
  async (id: string | number) => {
    const requestUrl = `${apiUrl}/${id}`;
    return axios.get<IMaintenanceVehicle>(requestUrl);
  },
  { serializeError: serializeAxiosError }
);

export const createEntity = createAsyncThunk(
  'maintenanceVehicleDTO/create_entity',
  async (entity: IMaintenanceVehicleDTO, thunkAPI) => {
    const result = await axios.post<IMaintenanceVehicleDTO>(apiUrl, cleanEntity(entity));
    thunkAPI.dispatch(getEntities({}));
    return result;
  },
  { serializeError: serializeAxiosError }
);

export const updateEntity = createAsyncThunk(
  'maintenanceVehicleDTO/update_entity',
  async (entity: IMaintenanceVehicleDTO, thunkAPI) => {
    const result = await axios.put<IMaintenanceVehicleDTO>(`${apiUrl}/${entity.id}`, cleanEntity(entity));
    thunkAPI.dispatch(getEntities({}));
    return result;
  },
  { serializeError: serializeAxiosError }
);

export const partialUpdateEntity = createAsyncThunk(
  'maintenanceVehicle/partial_update_entity',
  async (entity: IMaintenanceVehicle, thunkAPI) => {
    const result = await axios.patch<IMaintenanceVehicle>(`${apiUrl}/${entity.id}`, cleanEntity(entity));
    thunkAPI.dispatch(getEntities({}));
    return result;
  },
  { serializeError: serializeAxiosError }
);

export const deleteEntity = createAsyncThunk(
  'maintenanceVehicleDTO/delete_entity',
  async (id: string | number, thunkAPI) => {
    const requestUrl = `${apiUrl}/${id}`;
    const result = await axios.delete<IMaintenanceVehicleDTO>(requestUrl);
    thunkAPI.dispatch(getEntities({}));
    return result;
  },
  { serializeError: serializeAxiosError }
);

// slice

export const MaintenanceVehicleCustomSlice = createSlice({
  name: 'maintenanceVehicleCustom',
  initialState: initialState as MaintenanceVehicleDTOState,
  reducers: {
    reset() {
      return initialState;
    },
  },
  extraReducers(builder) {
    builder
      .addCase(getMaintenanceVehicleByMaintenanceControlIdAndTruckImplementId.fulfilled, (state, action) => {
        state.loading = false;
        state.maintenanceVehicleDTO = action.payload.data;
      })
      .addCase(getMaintenanceVehicleById.fulfilled, (state, action) => {
        state.loading = false;
        state.maintenanceVehicleDTO = action.payload.data;
      })
      .addCase(getEntity.fulfilled, (state, action) => {
        state.loading = false;
        state.entity = action.payload.data;
      })
      .addCase(deleteEntity.fulfilled, state => {
        state.updating = false;
        state.updateSuccess = true;
        state.entity = {};
      })
      .addMatcher(isFulfilled(getEntities), (state, action) => {
        return {
          ...state,
          loading: false,
          entities: action.payload.data,
          totalItems: parseInt(action.payload.headers['x-total-count'], 10),
        };
      })
      .addMatcher(isFulfilled(createEntity, updateEntity, partialUpdateEntity), (state, action) => {
        state.updating = false;
        state.loading = false;
        state.updateSuccess = true;
        state.maintenanceVehicleDTO = action.payload.data;
      })
      .addMatcher(isFulfilled(partialUpdateEntity), (state, action) => {
        state.updating = false;
        state.loading = false;
        state.updateSuccess = true;
        state.entity = action.payload.data;
      })
      .addMatcher(isPending(getEntities, getEntity, getMaintenanceVehicleByMaintenanceControlIdAndTruckImplementId, getMaintenanceVehicleById), state => {
        state.errorMessage = null;
        state.updateSuccess = false;
        state.loading = true;
      })
      .addMatcher(isPending(createEntity, updateEntity, partialUpdateEntity, deleteEntity), state => {
        state.errorMessage = null;
        state.updateSuccess = false;
        state.updating = true;
      });
  },
});

export const { reset } = MaintenanceVehicleCustomSlice.actions;

// Reducer
export default MaintenanceVehicleCustomSlice.reducer;
